import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface SearchContextType {
  /** The active search query (empty string = no search, show full inbox) */
  query: string;
  /** Set the query without triggering a search (as the user types) */
  setQuery: (q: string) => void;
  /** The committed query that the inbox should filter by */
  activeQuery: string;
  /** Commit the current query — inbox will fetch search results */
  submitSearch: () => void;
  /** Clear the search and return to the full inbox */
  clearSearch: () => void;
}

const SearchContext = createContext<SearchContextType>({
  query: '',
  setQuery: () => {},
  activeQuery: '',
  submitSearch: () => {},
  clearSearch: () => {},
});

export function useSearch(): SearchContextType {
  return useContext(SearchContext);
}

/**
 * Provides inline search state shared between the top-nav search bar
 * and the InboxView. Searching filters the inbox in place rather than
 * navigating to a separate results page.
 */
export function SearchProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');

  const submitSearch = useCallback(() => {
    setActiveQuery(query.trim());
  }, [query]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setActiveQuery('');
  }, []);

  return (
    <SearchContext.Provider value={{ query, setQuery, activeQuery, submitSearch, clearSearch }}>
      {children}
    </SearchContext.Provider>
  );
}
